import { boot } from '@dazn/bootstrap-lib';

import { SafeModeConfig } from '../configuration';
import {
    ANONYMOUS_TOKEN_LSKEY,
    AUTH_TOKEN_LSKEY,
    BRANDS,
    TOKEN_EXP_LIMIT,
} from '../configuration/constants';

import { getCustomFeatureFlags } from '../configuration/customFeatureFlags';

import { SAFEMODE_STAUS } from '../constants/safemode.constants';
import { captureException } from '../error-handling/captureException';
import { fetchSafeModeAsync } from '../error-handling/createErrorHandlingModule';

import { FeaturesModule } from '../fflag/createFeatureFlagModule';

import { InitializationModule } from '../initialization';

import { AnonymousToken, GetDevMode, GetGuid } from './common-types';
import { fetchUserProfile } from './fetchUserProfile';
import { getGeoZones } from './getGeoZones';
import { getHeaders } from './getHeaders';
import { logout } from './logout';
import { decodeToken, checkToken, HAS_SENT_APP_LAUNCH_RAT } from './token';

const isProfileValid = (
    userProfile: boot.user.UserProfile | Record<string, never>,
) => Object.keys(userProfile || {}).length > 0;

namespace createUserModule {
    export interface Params {
        /**
         * URL for the auth token service endpoint
         */
        authTokenRefreshUrl?: () => string;
        /**
         * URL for the user profile service endpoint
         */
        userProfileUrl: () => string;
        /**
         * Getter for the URL of the logout service
         */
        logOutUrl: () => string;
        /**
         * To load the token from storage
         */
        load: () => Promise<string | null>;
        /**
         * To save the token to storage. We invoke this with token value.
         */
        save: (token: string) => Promise<unknown>;
        /**
         * To remove the token from storage
         */
        remove: () => Promise<void>;

        /**
         * Callback that is invoked if the user has requested to delete the token
         */
        onTokenReset: () => void;
        /**
         * Session identifier
         */
        sessionId: () => string;
        /**
         * Device identifier
         */
        deviceId: () => string;
        /**
         * HTTP client that follows the Fetch API specification {@link https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API}
         */
        fetch: boot.util.Fetch;
        /**
         * Globally Unique Identifier
         */
        getGuid: GetGuid;
        /**
         * Returns the status of the dev-mode feature
         */
        getDevMode: GetDevMode;
        /**
         * Returns URL for a startup service
         */
        getApiUrl?: (serviceName: string, ver: string) => string | undefined;
        defaultActiveUserTypes: string[];
        getLanguage: () => string;
        environment: boot.config.Environment;
        geoCountry: () => boot.config.AnyCountry;
        getNativeCountryOrMoonOrMena: (
            country: boot.config.AnyCountry,
        ) => boot.config.NativeCountryOrMoonOrMena;
        getSafeModeConfig: () => SafeModeConfig;
        getDeviceId: () => string;
        getBrand: () => boot.brand.BrandName;
        featuresModule: FeaturesModule;
    }
}

const ONE_SECOND_MS = 1000;
const FIVE_MINUTES_MS = 300000;
const stringifyJWTTimestamp = (ts: number) =>
    new Date(ts * ONE_SECOND_MS).toISOString();

/**
 * The user module has two main concerns:

 1. Encapsulating logic and private state for the user token
 2. Encapsulating logic and private state for the user profile

 Note: The module holds user / token references in memory, but persistence is handled through `load`, `save` and `remove` callbacks.

 One of the best ways to understand the module, and it's uses is to read the integration (`./integration`) tests.

 It is _strongly advised_ that all consumers (bootstrap and chapters) use the convinience methods instead of retrieving the full token / profile. Direct access of the token / profile leaves the consumer susceptible to changes in the token / profile API and increases maintenance costs and coupling.
 */
function createUserModule({
    authTokenRefreshUrl,
    userProfileUrl,
    logOutUrl,
    load,
    save,
    remove,
    onTokenReset,
    sessionId,
    deviceId,
    fetch,
    getGuid,
    getDevMode,
    getApiUrl,
    defaultActiveUserTypes,
    getLanguage,
    environment,
    geoCountry,
    getNativeCountryOrMoonOrMena,
    getSafeModeConfig,
    getDeviceId,
    getBrand,
    featuresModule,
}: createUserModule.Params) {
    let cachedUserProfile: boot.user.UserProfile | Record<string, never> = {};
    let cachedUserToken = '';
    let cachedAnonymousToken =
        window.localStorage.getItem(ANONYMOUS_TOKEN_LSKEY) ?? '';
    let decodedUserToken: boot.user.UserToken | null = null;
    let decodedAnonymousToken: AnonymousToken | null = cachedAnonymousToken
        ? decodeToken(cachedAnonymousToken)
        : null;
    let isProfileCached = false;
    let initializationModule: InitializationModule;

    // Helper to get custom headers for API calls
    const getCustomHeadersForAPI = () => ({
        isCustomHeadersEnabled:
            featuresModule.isEnabledAnonymous('f_ar_custom_common_headers') ??
            false,
        brandName: getBrand(),
        daznId: decodedUserToken?.user,
    });

    const internalCacheAndParseToken = (token: string) => {
        cachedUserToken = token;
        decodedUserToken = token ? decodeToken(token) : null;

        if (decodedUserToken) {
            window.newrelic?.setCustomAttribute(
                'contentCountry',
                decodedUserToken.contentCountry,
            );
            window.newrelic?.setCustomAttribute(
                'daznId',
                decodedUserToken.user,
            );
            window.newrelic?.setCustomAttribute(
                'userStatus',
                decodedUserToken.userstatus,
            );
            window.newrelic?.setCustomAttribute(
                'viewerId',
                decodedUserToken.viewerId,
            );
            window.newrelic?.setCustomAttribute(
                'jwt.expires',
                decodedUserToken.exp,
            );
            window.newrelic?.setCustomAttribute(
                'jwt.issued',
                decodedUserToken.issued,
            );
            window.newrelic?.setCustomAttribute(
                'jwt.expiredISO',
                stringifyJWTTimestamp(decodedUserToken.exp),
            );
            window.newrelic?.setCustomAttribute(
                'jwt.issuedISO',
                stringifyJWTTimestamp(decodedUserToken.issued),
            );

            const geoZones = getGeoZones(decodedUserToken.geo);
            if (geoZones) {
                window.newrelic?.setCustomAttribute('zones', geoZones);
            }
        }
    };

    const getValidatedToken = () => {
        if (!cachedUserToken) throw new Error('Cached user token is missing.');

        const ms = 1000;

        const decodedToken = decodeToken(cachedUserToken);

        const exp = Math.floor(decodedToken.exp / ms);

        const currentTime = Math.floor(Date.now() / ms);

        if (exp - currentTime > TOKEN_EXP_LIMIT)
            return Promise.resolve(cachedUserToken);

        // need to call RAT
        const refreshAccessTokenUrl = getApiUrl
            ? getApiUrl('RefreshAccessToken', 'v5')
            : '';

        if (!refreshAccessTokenUrl)
            throw new Error(`RefreshAccessToken url is missing,
        Check getApiUrl, you can get it from startup module.`);

        return checkToken(
            cachedUserToken,
            refreshAccessTokenUrl,
            sessionId(),
            deviceId(),
            fetch,
            getGuid,
            getDevMode,
            getCustomHeadersForAPI(),
        );
    };

    const internalSaveToken = (token: string) =>
        save(token)
            .then(() => internalCacheAndParseToken(token))
            .then(() => token);

    const setProfile = (newUserProfile = {}) => {
        cachedUserProfile = newUserProfile;
        isProfileCached = isProfileValid(cachedUserProfile);

        if (newUserProfile.hasOwnProperty('UserCountryCode')) {
            window.newrelic?.setCustomAttribute(
                'billingCountry',
                (
                    newUserProfile as boot.user.UserProfile
                ).UserCountryCode.toLowerCase(),
            );
        }

        return cachedUserProfile;
    };
    const internalFetchProfile = (
        token: string,
        updateProfileAsync?: boolean,
    ) =>
        fetchUserProfile(
            `${userProfileUrl()}/UserProfile`,
            token,
            fetch,
            getGuid,
            getDevMode,
            setProfile,
            updateProfileAsync,
            getCustomHeadersForAPI(),
        );

    const hasDecodedUserToken = () => Boolean(decodedUserToken);

    let refreshTimeOut: number;
    const getTokensTTL = (decodedToken: boot.user.UserToken) =>
        decodedToken.exp * ONE_SECOND_MS - FIVE_MINUTES_MS - Date.now();

    const isPast = (decodedToken: boot.user.UserToken) =>
        Date.now() < decodedToken.issued * ONE_SECOND_MS;

    const refreshExistingTokenAndUpdateProfile = ({
        force = false,
        fromMainThread = false,
    }: { force?: boolean; fromMainThread?: boolean } | void = {}) =>
        load().then(localStorageToken => {
            if (!localStorageToken) {
                return undefined;
            }

            const decodedToken = decodeToken(
                localStorageToken,
            ) as boot.user.UserToken;

            const tokenTTL = getTokensTTL(decodedToken);

            // Basic validity checks
            if (
                decodedToken &&
                tokenTTL >= 0 &&
                !Number.isNaN(tokenTTL) &&
                fromMainThread
            ) {
                // setting cached token and profile to window.dazn
                const key = `userprofile_${decodedToken.user}_${decodedToken.contentCountry}`;
                const userProfileFromLocalStorage = JSON.parse(
                    localStorage.getItem(key) || '{}',
                );
                const { profile } = userProfileFromLocalStorage;
                return Promise.resolve()
                    .then(() => internalSaveToken(localStorageToken))
                    .then(() =>
                        profile
                            ? setProfile(profile)
                            : internalFetchProfile(localStorageToken)
                                  .then(setProfile)
                                  .catch(error => {
                                      captureException(error);
                                  }),
                    )
                    .then(() => undefined);
            }

            const refreshAccessTokenUrl = getApiUrl
                ? getApiUrl('RefreshAccessToken', 'v5')
                : authTokenRefreshUrl?.();
            if (!refreshAccessTokenUrl) {
                throw new Error(
                    `
        RefreshAccessToken url is missing,
        Check getApiUrl, you can get it from startup module.
                                `,
                );
            }
            const fetchToken = () =>
                checkToken(
                    localStorageToken,
                    refreshAccessTokenUrl,
                    sessionId(),
                    deviceId(),
                    fetch,
                    getGuid,
                    getDevMode,
                    getCustomHeadersForAPI(),
                );
            return fetchToken()
                .then(internalSaveToken)
                .then(token => {
                    const freshDecodedToken = decodeToken(token);
                    let timeout = getTokensTTL(freshDecodedToken);
                    const maxTimeout = 2147483647;
                    const defaultTokenTimeout = 3600000; // 1 hour
                    if (refreshTimeOut) {
                        window.clearTimeout(refreshTimeOut);
                    }

                    if (
                        isPast(freshDecodedToken) ||
                        timeout >= maxTimeout ||
                        timeout <= 0 ||
                        timeout !== timeout // NaN
                    ) {
                        timeout = defaultTokenTimeout;
                    }
                    refreshTimeOut = window.setTimeout(
                        refreshExistingTokenAndUpdateProfile,
                        timeout,
                    );
                    return token;
                })
                .then(token => {
                    if (!force) {
                        return undefined;
                    }

                    return internalFetchProfile(token)
                        .then(setProfile)
                        .then(() => undefined)
                        .catch(captureException);
                });
        });

    const setAnonymousToken = (token: string) => {
        cachedAnonymousToken = token;
        window.localStorage.setItem(ANONYMOUS_TOKEN_LSKEY, token);
        decodedAnonymousToken = cachedAnonymousToken
            ? decodeToken(cachedAnonymousToken)
            : null;
    };
    let isAnonymousTokeRequested = false;

    // eslint-disable-next-line @applitools/compat/no-async-await
    const anonymousToken = async () => {
        isAnonymousTokeRequested = true;
        const apiBody = {
            deviceId: deviceId(),
            languageCode: getLanguage(),
            platform: 'web',
            kountFingerPrint: deviceId(),
        };
        try {
            const apiUrl: string = getApiUrl
                ? (getApiUrl('AnonymousToken', 'v1') as string)
                : '';
            const res = await fetch(apiUrl, {
                method: 'POST',
                headers: getHeaders(
                    '',
                    getGuid,
                    getDevMode,
                    undefined,
                    undefined,
                    getCustomHeadersForAPI(),
                ),
                body: JSON.stringify(apiBody),
            });
            const data = await res.json();
            setAnonymousToken(data.token);
        } catch (err) {
            console.error(
                '[BOOTSTRAP] Anonymous Token Generation Error',
                String(err),
            );
        }
    };

    const isAnonPlaybackFeatureEnabled = (country: string) => {
        const params = new URLSearchParams(window.location.search);
        const isFromOneBoxSeo =
            params && params.get('utm_source')?.toLowerCase() === 'onebox';
        const isEnabledForEndUsers = getCustomFeatureFlags(
            'cff_anonymous_playback',
            environment,
            getNativeCountryOrMoonOrMena(country)?.toUpperCase(),
        );

        return isEnabledForEndUsers || isFromOneBoxSeo;
    };

    const shouldRefreshAnonToken = (geoCountryValue: string) => {
        const anonToken = window.localStorage.getItem(ANONYMOUS_TOKEN_LSKEY);
        const thresholdExpiry = 3600;
        const timeDenominator = 1000;
        const currentTimestamp = Math.floor(Date.now() / timeDenominator);
        if (!anonToken) return true;

        const decodedToken = decodeToken(anonToken);
        if (!decodedToken?.exp) return true;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        const tokenContentCountry = decodedToken?.contentCountry?.toLowerCase();
        if (tokenContentCountry !== geoCountryValue) {
            return true;
        }

        return decodedToken.exp - currentTimestamp < thresholdExpiry;
    };

    const shouldRefreshAnonymousToken = (country: string) =>
        isAnonPlaybackFeatureEnabled(country) &&
        shouldRefreshAnonToken(country) &&
        !window.localStorage.getItem(AUTH_TOKEN_LSKEY) &&
        !isAnonymousTokeRequested;

    function checkSafeModeRedirection() {
        const config = getSafeModeConfig();
        const liteUrl = config.homePageURL;
        const deviceId = getDeviceId() ?? '';

        fetchSafeModeAsync({
            getSafeModeConfig,
            fetch,
            getBrandName: getBrand,
        }).then(response => {
            const isSafeModeOn = response.status === SAFEMODE_STAUS.RED;

            if (isSafeModeOn) {
                try {
                    const liteUrlInstance = new URL(liteUrl);
                    const { user } = decodedUserToken || {};
                    liteUrlInstance.searchParams.append('deviceId', deviceId);
                    if (user) {
                        liteUrlInstance.searchParams.append('daznId', user);
                    }
                    window.newrelic?.addPageAction('SafeModeRedirection');
                    window.location.replace(liteUrlInstance.href);
                } catch (err) {
                    captureException(err);
                }
            }
        });
    }

    const removeAffiliates = () => {
        let affiliatesToRemove: string[] = [];
        type ExpiryConfig = {
            expiryInDays: number;
            expiryInMinutes: number;
        };
        type AffiliateConfig = Record<string, ExpiryConfig>;

        const AFFILIATE_PREFIX = 'affiliate_';
        const AFFILIATE_EXPIRY_SUFFIX = '_expiry';

        try {
            const affiliateConfigsArray = JSON.parse(
                featuresModule.getVariableStringAuthenticated(
                    'f_ar_whitelist_affiliate_tracking_params',
                    'affiliateParamsConfig',
                ) ?? '[]',
            ) as AffiliateConfig[];

            affiliatesToRemove = affiliateConfigsArray.flatMap(obj =>
                Object.keys(obj),
            );

            affiliatesToRemove?.forEach(affiliate => {
                localStorage.removeItem(`${AFFILIATE_PREFIX}${affiliate}`);
                localStorage.removeItem(
                    `${AFFILIATE_PREFIX}${affiliate}${AFFILIATE_EXPIRY_SUFFIX}`,
                );
            });
            localStorage.removeItem(`giftCode`);
        } catch (err) {
            captureException(err);
        }
    };

    const reinitializeOnTokenSetToEmpty = () => {
        initializationModule.run();
    };

    return {
        /**
         * To run the module
         */
        run: ({
            _initializationModule,
        }: {
            _initializationModule: InitializationModule;
        }) => {
            initializationModule = _initializationModule;
            if (
                [BRANDS.DAZN, BRANDS.FOXSPORTS].includes(getBrand()) &&
                shouldRefreshAnonymousToken(geoCountry()?.toLowerCase())
            ) {
                anonymousToken();
            }
            checkSafeModeRedirection();

            window.addEventListener('storage', (event: StorageEvent) => {
                if (
                    event.key === AUTH_TOKEN_LSKEY &&
                    !event.oldValue &&
                    event.newValue
                ) {
                    window.location.reload();
                }
            });

            return refreshExistingTokenAndUpdateProfile({
                fromMainThread: true,
            });
        },

        refreshExistingTokenAndUpdateProfile,
        /**
         * Retrieves the current user profile object
         */
        getProfile: () => cachedUserProfile,
        /**
         * Sets the current user profile object
         */
        setProfile,
        /**
         * Gets user language from the users profile
         */
        getUserLanguage: () => cachedUserProfile.UserLanguageLocaleKey,
        /**
         * Gets an array of supported languages for the user
         */
        getSupportedLanguages: () => cachedUserProfile.SupportedLanguages,
        /**
         * Checks if the user profile is cached
         */
        isProfileCached: () => isProfileCached,
        /**
         * Retrieve the current token
         */
        getToken: () => cachedUserToken,
        /**
         * Retrieve the current anonymous token
         */
        getAnonymousToken: () => cachedAnonymousToken,
        /**
         * set the current anonymous token
         */
        setAnonymousToken,
        getDecodedJWT: () => decodedUserToken,
        getDecodedAnonymousToken: () => decodedAnonymousToken,
        /**
         * Set a given token
         */
        setToken: (
            newToken: string | null,
            options?: { updateProfileAsync?: boolean },
        ) => {
            if (newToken === cachedUserToken) {
                if (options?.updateProfileAsync) {
                    return internalFetchProfile(
                        newToken,
                        options?.updateProfileAsync,
                    ).catch(captureException);
                }
                return Promise.resolve();
            }

            if (newToken) {
                document.cookie = 'isAuthenticated=true; path=/;';
                return internalSaveToken(newToken)
                    .then(() =>
                        internalFetchProfile(
                            newToken,
                            options?.updateProfileAsync,
                        )
                            .then(setProfile)
                            .catch(error => {
                                captureException(error);
                            }),
                    )
                    .then(() => undefined);
            }

            cachedUserProfile = {};
            isProfileCached = false;
            window?.sessionStorage?.removeItem(HAS_SENT_APP_LAUNCH_RAT);

            const previousToken = cachedUserToken;
            const brand = (
                window?.dazn?.brand?.brandName as string
            )?.toUpperCase();
            const brandName =
                brand && brand !== BRANDS.DAZN.toUpperCase() ? brand : '';
            internalCacheAndParseToken('');
            document.cookie =
                'isAuthenticated=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            return remove()
                .then(() => {
                    if (!previousToken) {
                        return undefined;
                    }

                    removeAffiliates();

                    return logout(
                        logOutUrl(),
                        previousToken,
                        deviceId(),
                        fetch,
                        getGuid,
                        getDevMode,
                        brandName,
                        getCustomHeadersForAPI(),
                    ).then(() => {
                        onTokenReset();
                        reinitializeOnTokenSetToEmpty();
                    });
                })
                .catch(() => {
                    onTokenReset();
                    reinitializeOnTokenSetToEmpty();
                });
        },
        /**
         * Is user authorized (a token convenience method)
         */
        isAuthorized: hasDecodedUserToken,
        /**
         * Validates if `userType` specificed in user token matches the one in the user statuses
         */
        isActive: (chapterUserTypes = defaultActiveUserTypes) =>
            hasDecodedUserToken() &&
            chapterUserTypes.includes(decodedUserToken?.userstatus || ''),

        isVip: () => (hasDecodedUserToken() && decodedUserToken?.vip) || false,
        /**
         * Retrieves signup country from the current user profile object
         */
        getSignUpCountry: () => cachedUserProfile.UserCountryCode || '',
        /**
         * Retrieves country from token (a token convenience method)
         */
        getCountry: () =>
            decodedUserToken?.country ||
            decodedAnonymousToken?.country?.toLowerCase() ||
            geoCountry()?.toLowerCase() ||
            '',
        /**
         * Retrieves User home country currency from token (a token convenience method)
         */
        getHomeCurrency: () =>
            (boot.config.countryCurrencyCodes as Record<string, string>)[
                decodedUserToken?.homeCountry || ''
            ] || '',
        /**
         * Retrieves content country from token (a token convenience method)
         */
        getContentCountry: () =>
            decodedUserToken?.contentCountry ||
            decodedAnonymousToken?.contentCountry?.toLowerCase() ||
            geoCountry()?.toLowerCase() ||
            '',
        /**
         * Retrieves user status from token (a token convenience method)
         */
        getUserStatus: () => decodedUserToken?.userstatus || '',
        /**
         * Retrieves user type from token (a token convenience method)
         */
        getUserType: () => decodedUserToken?.userType || NaN,
        getValidatedToken,
        /**
         * Forces a refresh of the user profile
         */
        fetchProfile: () => {
            // @TODO: Should we remove this fetchProfile entirely ?
            // Suspecting that shouldFetch will always be false
            const shouldFetch = hasDecodedUserToken() && !isProfileCached;
            if (!shouldFetch) {
                return Promise.resolve(undefined);
            }
            return load().then(tokenFromLocalStorage => {
                if (!tokenFromLocalStorage) {
                    return undefined;
                }
                return internalFetchProfile(tokenFromLocalStorage)
                    .then(setProfile)
                    .then(() => isProfileCached);
            });
        },
    };
}

export { createUserModule };
